import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Autocomplete,
    Box,
    Collapse,
    Grid,
    Grow,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { commonSort } from "../../common";
import AddButton from "../../components/AddButton/AddButton";
import SearchBar from "../../components/SearchBar/SearchBar";
import TableButtons from "../../components/Button/TableButtons";
import LoadingBackdrop from "../../components/LoadingBackdrop/LoadingBackdrop";
import Modal from "../../components/Modal/Modal";
import { StyledTableDiv } from "../../components/StyledComponents/StyledComponents";
import { getUserGroups, addUserGroup, updateUserGroup, deleteUserGroup, getPlants } from "../../store/actions/userGroupActions";

export default function UserGroup() {
    const { t } = useTranslation("translation");

    const dispatch = useDispatch();
    const [initialRows, setInitialRows] = useState([]);
    let [rows, setRows] = useState([]);
    const [pageLoaded, setPageLoaded] = useState(false);

    const { UserGroup } = useSelector((UserGroup) => UserGroup);

    useEffect(() => {
        getPlantCodeHandler();
        loadHandler();
    }, []);

    useEffect(() => {
        if (UserGroup?.userGroups?.length) {
            setInitialRows(UserGroup.userGroups);
            setRows(UserGroup.userGroups);
            UserGroup.userGroups = {};
            setPageLoaded(true);
        }
        if (UserGroup?.plants?.resultObject) {
            setPlantList(UserGroup.plants.resultObject);
        }
        if (UserGroup.isTaskUpdatedUserGroup) {
            setEditableRow(false);
            setDisableButton(false);
            loadHandler();
        }
    }, [UserGroup]);

    const loadHandler = () => {
        dispatch(getUserGroups());
    };

    // Get plant codes
    const [plantList, setPlantList] = useState([]);
    const getPlantCodeHandler = () => {
        dispatch(getPlants());
    };

    // Update and Add in one function
    const updateAddHandler = (row) => {
        // Update
        if (row.id !== "") {
            let data = {
                id: row.id,
                groupCode: row.groupCode,
                groupName: row.groupName,
                plantId: row.plantId,
                status: 1,
            };
            dispatch(updateUserGroup(data));
        }
        // Add
        else {
            let data = {
                groupCode: row.groupCode,
                groupName: row.groupName,
                plantId: addPlantID,
            };
            dispatch(addUserGroup(data));
        }
    };

    // Delete row
    const deleteHandler = (row) => {
        toggleModal();
        const deletePlantID = plantList.filter((item) => item.code === row.plantCode)[0].id;
        let data = {
            id: row.id,
            groupCode: row.groupCode,
            groupName: row.groupName,
            plantId: deletePlantID,
        };
        dispatch(deleteUserGroup(data));
    };

    // Add blank row for new plant addition
    const addBlankRowHandler = () => {
        setAddedPlantName("");
        setAddedPlantID(0);
        setCurrentPage(0);
        setDisableButton(true);
        let addedBlankRow = [
            {
                id: "",
                groupCode: "",
                groupName: "",
                plantCode: "",
                plantName: "",
                usersOfGroup: [
                    {
                        id: 0,
                        userCode: null,
                        userName: "",
                        department: null,
                        profilePictureUrl: "",
                        plantId: 0,
                    },
                ],
            },
        ];
        rows = addedBlankRow.concat(rows);
        setEditableRow(addedBlankRow[0].id);
        setEditable(true);
        setRows(rows);

        setError({
            userGroupCodeText: t("EnterAUserGroupCode"),
            userGroupNameText: t("EnterAUserGroupName"),
            plantText: t("SelectAPlant"),
        });
    };

    // Expand row
    const [openRow, setOpenRow] = useState(false);
    const [expandID, setExpandId] = useState("");
    const expandRow = (rowId) => {
        if (openRow && expandID !== rowId) {
            setExpandId(rowId);
        } else {
            setOpenRow(!openRow);
            setExpandId(rowId);
        }
    };

    // Set plantID
    let [addPlantID, setAddedPlantID] = useState(0);
    let [addPlantName, setAddedPlantName] = useState("");
    const newPlantID = (plantCode) => {
        if (plantCode != null) {
            const filteredPlant = plantList.filter((item) => item.code === plantCode);
            setAddedPlantID(filteredPlant[0].id);
            setAddedPlantName(filteredPlant[0].name);
            return [filteredPlant[0].id, filteredPlant[0].name];
        } else {
            setAddedPlantID(0);
            setAddedPlantName("");
        }
    };

    // Filter table
    const [filterText, setFilterText] = useState("");
    const filterData = (value) => {
        const newFilteredText = value.toLowerCase();
        setFilterText(newFilteredText);
        let filteredItems = cloneObject(initialRows).filter((item) => item.groupCode.toLowerCase().includes(newFilteredText) || item.groupName.toLowerCase().includes(newFilteredText));
        setRows(filteredItems);
        setDisableButton(false);
    };

    useEffect(() => {
        filterData(filterText);
    }, [initialRows]);

    // Sort function
    let [sortOrder, setSortOrder] = useState();
    let [sortColumn, setSortColumn] = useState();
    const handleSort = (row, column) => {
        const sortedList = commonSort(row, column);
        setSortOrder(sortedList[0]);
        setSortColumn(sortedList[1]);
        setInitialRows(sortedList[2]);
    };

    // Pagination
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const handleChangePage = (_event, newPage) => {
        setCurrentPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setCurrentPage(0);
    };

    // Page redirect
    let history = useHistory();
    const openDetailPage = (id) => {
        history.push("/addusergrouptopool?id=" + id);
    };

    // Set edited rows
    const setRowHandler = (value, obj, updatedRow) => {
        const index = rows.indexOf(updatedRow);
        if (updatedRow.id !== "") {
            const row = rows[index];
            row[obj] = value;
            rows[index] = row;
            setRows(rows);
        } else {
            const row = rows[index];
            row[obj] = value;
            setRows(rows);
        }

        // Validation
        if (value.length > 0) {
            if (obj === "groupCode") {
                setError({ ...error, userGroupCodeText: "" });
            } else if (obj === "groupName") {
                setError({ ...error, userGroupNameText: "" });
            }
        } else {
            if (obj === "groupCode") {
                setError({ ...error, userGroupCodeText: t("EnterAUserGroupCode") });
            } else if (obj === "groupName") {
                setError({ ...error, userGroupNameText: t("EnterAUserGroupName") });
            }
        }
    };

    const cloneObject = (obj) => {
        return JSON.parse(JSON.stringify(obj));
    };

    // Edit rows
    const [editable, setEditable] = useState(false);
    const [editableRow, setEditableRow] = useState("");
    const enableEditHandler = (row) => {
        if (disableButton || editable) {
            filterData(filterText);
        }
        setEditableRow(row.id);
        setEditable(true);

        setError({
            userGroupCodeText: "",
            userGroupNameText: "",
            plantText: "",
        });
    };

    // Enable disable add button
    const [disableButton, setDisableButton] = useState(false);
    const disableEditHandler = () => {
        filterData(filterText);
        setEditable(false);
        setDisableButton(false);
        setAddedPlantID(0);

        setError({
            userGroupCodeText: t("EnterAUserGroupCode"),
            userGroupNameText: t("EnterAUserGroupName"),
            plantText: t("SelectAPlant"),
        });
    };

    // Delete Modal
    const [isModalOpen, setModalOpen] = useState(false);
    const [deletedRow, setDeletedRow] = useState([]);
    const deleteButtonHandler = (row) => {
        setDeletedRow(row);
        toggleModal();
    };

    const toggleModal = () => {
        setModalOpen((prevState) => !prevState);
    };

    const renderTableCell = (rowItems, isSortingAction = true) => {
        const directionType = sortOrder === 1 ? "asc" : "desc";
        return rowItems.map((item, index) => {
            if (item.id === "Actions") {
                return (
                    <TableCell id={`ug_rowItems_TableCell_Actions_${index}_${item.id}`} align="center">
                        {t("Actions")}
                    </TableCell>
                );
            } else if (item.id === "") {
                return <TableCell id={`ug_rowItems_TableCell_${index}_${item.id}`} align="center"></TableCell>;
            } else {
                return isSortingAction ? (
                    <TableCell id={`ug_rowItems_TableCell_${index}_${item.id}`} align="center">
                        <TableSortLabel
                            id={`ug_rowItems_TableSortLabel_${index}_${item.id}`}
                            active={sortColumn === item.id}
                            direction={directionType}
                            onClick={() => handleSort(cloneObject(initialRows), item.id)}
                            style={{ paddingLeft: 25 }}
                        >
                            {item.label}
                        </TableSortLabel>
                    </TableCell>
                ) : (
                    <TableCell align="center">{item.label}</TableCell>
                );
            }
        });
    };

    // Validation
    const [error, setError] = useState({
        userGroupCodeText: t("EnterAUserGroupCode"),
        userGroupNameText: t("EnterAUserGroupName"),
        plantText: t("SelectAPlant"),
    });
    const [isAddDisable, setIsAddDisable] = useState(true);

    useEffect(() => {
        if (addPlantID !== 0) {
            setError({ ...error, plantText: "" });
        } else {
            setError({ ...error, plantText: t("SelectAPlant") });
        }
    }, [addPlantID]);

    useEffect(() => {
        if (error.userGroupCodeText === "" && error.userGroupNameText === "" && error.plantText === "") {
            setIsAddDisable(false);
        } else {
            setIsAddDisable(true);
        }
    }, [error]);

    return (
        <StyledTableDiv>
            <h1>
                {t("UserGroup")} {t("List")}{" "}
            </h1>
            <AddButton onClick={() => addBlankRowHandler()} disabled={disableButton} />
            {pageLoaded === true ? (
                <Grid container spacing={4} alignItems="center" justifyContent="center" sx={{ marginBottom: 4 }}>
                    <Grid item xs={12} sm={12} md={4}>
                        <SearchBar value={filterText} onChange={(newValue) => setFilterText(newValue)} onRequestSearch={(value) => filterData(value)} />
                    </Grid>
                </Grid>
            ) : null}
            <Box m={2} />
            {rows.length > 0 ? (
                <Grow in={true} style={{ transformOrigin: "0 0 0" }} {...{ timeout: 1000 }}>
                    <TableContainer id="ug_TableContainer" component={Paper}>
                        <Table id="ug_Table" aria-label="collapsible table">
                            <TableHead id="ug_TableHead">
                                <TableRow id="ug_TableRow">
                                    {renderTableCell([
                                        { id: "", label: "" },
                                        { id: "id", label: t("ID") },
                                        { id: "NumberOfUsers", label: t("NumberOfUsers") },
                                        { id: "groupCode", label: t("UserGroupCode") },
                                        { id: "groupName", label: t("UserGroupName") },
                                        { id: "plantCode", label: t("PlantCode") },
                                        { id: "plantName", label: t("PlantName") },
                                        { id: "Actions", label: t("Actions") },
                                    ])}
                                </TableRow>
                            </TableHead>
                            <TableBody id="ug_TableBody">
                                {rows.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage).map((row, index) => (
                                    <React.Fragment key={row.id}>
                                        <TableRow id={`ug_TableRow_${index}`}>
                                            <TableCell id={`ug_TableCell_${index}`}>
                                                <IconButton id={`ug_TableCell_IconButton_${index}`} aria-label="expand row" size="small" onClick={() => expandRow(row.id)}>
                                                    {openRow && expandID === row.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell id={`ug_TableCell_1_${index}`} align="center">
                                                {row.id}
                                            </TableCell>
                                            <TableCell id={`ug_TableCell_2_${index}`} align="center">
                                                {row.usersOfGroup ? row.usersOfGroup.length : null}
                                            </TableCell>
                                            <TableCell id={`ug_TableCell_3_${index}`} align="center">
                                                {editable && editableRow === row.id ? (
                                                    <TextField
                                                        id={`ug_TableCell_3_TextField_${index}`}
                                                        onInput={(e) => setRowHandler(e.target.value, "groupCode", row)}
                                                        defaultValue={row.groupCode}
                                                        variant="outlined"
                                                        error={error.userGroupCodeText.length > 0}
                                                        helperText={error.userGroupCodeText}
                                                    />
                                                ) : (
                                                    row.groupCode
                                                )}
                                            </TableCell>
                                            <TableCell id={`ug_TableCell_4_${index}`} align="center">
                                                {editable && editableRow === row.id ? (
                                                    <TextField
                                                        id={`ug_TableCell_4_TextField_${index}`}
                                                        onInput={(e) => setRowHandler(e.target.value, "groupName", row)}
                                                        defaultValue={row.groupName}
                                                        variant="outlined"
                                                        error={error.userGroupNameText.length > 0}
                                                        helperText={error.userGroupNameText}
                                                    />
                                                ) : (
                                                    row.groupName
                                                )}
                                            </TableCell>
                                            <TableCell id={`ug_TableCell_5_${index}`} align="center">
                                                {editable && editableRow === row.id && editableRow === "" ? (
                                                    <Autocomplete
                                                        id={`ug_TableCell_5_Autocomplete_${index}`}
                                                        aria-ruguired={true}
                                                        defaultValue={row.plantCode}
                                                        onChange={(_event, newValue) => {
                                                            newPlantID(newValue);
                                                        }}
                                                        options={plantList.map((option) => option.code)}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                id={`ug_TableCell_5_TextField_${index}`}
                                                                {...params}
                                                                variant="outlined"
                                                                error={error.plantText.length > 0}
                                                                helperText={error.plantText}
                                                            />
                                                        )}
                                                    />
                                                ) : (
                                                    row.plantCode
                                                )}
                                            </TableCell>
                                            <TableCell id={`ug_TableCell_6_${index}`} align="center">
                                                {" "}
                                                {editable && editableRow === row.id && editableRow === "" ? addPlantName : row.plantName}
                                            </TableCell>
                                            <TableButtons
                                                addDisabled={isAddDisable}
                                                editable={editable}
                                                editableRow={editableRow}
                                                row={row}
                                                updateAddHandler={() => updateAddHandler(row)}
                                                disableEditHandler={() => disableEditHandler()}
                                                enableEditHandler={() => enableEditHandler(row)}
                                                deleteButtonHandler={() => deleteButtonHandler(row)}
                                                openDetailPage={() => openDetailPage(row.id)}
                                                detail={true}
                                            />
                                        </TableRow>
                                        <TableRow id={`ug_TableRow_1_${index}`}>
                                            {expandID === row.id ? (
                                                <TableCell id={`ug_TableRow_TC_${index}`} style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                                                    <Collapse id={`ug_TableRow_Collapse_${index}`} in={openRow} timeout="auto" unmountOnExit>
                                                        <Box margin={1}>
                                                            <Typography id={`ug_TableRow_Typo_${index}`} variant="h6" gutterBottom component="div">
                                                                {t("Users")}
                                                            </Typography>
                                                            <Table id={`ug_TableRow_Table_${index}`} size="small">
                                                                <TableHead id={`ug_TableRow_TH_${index}`}>
                                                                    <TableRow id={`ug_TableRow_TR_${index}`}>
                                                                        {renderTableCell(
                                                                            [
                                                                                { id: "UserName", label: t("UserName") },
                                                                                { id: "UserRegistry", label: t("UserRegistry") },
                                                                            ],
                                                                            false
                                                                        )}
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody id={`ug_TableRow_TB_${index}`}>
                                                                    {row.usersOfGroup.map((detailRow) => (
                                                                        <TableRow id={`ug_TableRow_TB_TR_${index}`} key={detailRow.id}>
                                                                            <TableCell id={`ug_TableRow_TB_TC_${index}`} align="center">
                                                                                {detailRow.userName}
                                                                            </TableCell>
                                                                            <TableCell id={`ug_TableRow_TB_TC1_${index}`} align="center">
                                                                                {detailRow.userCode}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            ) : null}
                                        </TableRow>
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            id="ug_TablePagination"
                            rowsPerPageOptions={[20, 50, 100]}
                            component="div"
                            count={rows.length}
                            rowsPerPage={rowsPerPage}
                            page={currentPage}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </TableContainer>
                </Grow>
            ) : null}

            <Modal title={t("Delete")} content={t("WillYouDoTransaction")} approved={() => deleteHandler(deletedRow)} isModalOpen={isModalOpen} toggle={toggleModal} />

            <LoadingBackdrop   id="ug_lbd"/>
        </StyledTableDiv>
    );
}
